import { CommonModule, PlatformLocation } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  injectInfiniteQuery,
  injectQuery,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { MobileFiltersComponent } from 'libs/tournaments-v2/ui/filter/src/lib/mobile-filters/mobile-filters';
import { cloneDeep, merge } from 'lodash';
import { Observable, lastValueFrom, takeLast } from 'rxjs';

import { AppRouterPaths } from '@fantaz/core/router';
import { ProfileFacade } from '@fantaz/profile/data-access';
import { AuthFacade } from '@fantaz/shared/auth';
import { TournamentApiService } from '@fantaz/shared/data-access/api';
import {
  AllTournamentsPagination,
  CasualTournamentEntity,
  CurrentUser,
  FaceItemModel,
  Facet,
  FilterSelection,
  IPageModel,
  Profile,
  RoomFilterModel,
  TournamentTagsEnum,
  TournamentType,
  TournamentsQueryParams,
  UserPasses,
} from '@fantaz/shared/data-access/models';
import { BreadcrumbsService } from '@fantaz/shared/layout/breadcrumbs';
import { ShareModule, ShareService } from '@fantaz/shared/share';
import { EmptyListModule } from '@fantaz/shared/ui/empty-list';
import {
  Filters,
  RefinementList,
  Search,
  SortBy,
} from '@fantaz/shared/ui/filters';
import { MaterialModule } from '@fantaz/shared/ui/material';
import { SkeletonModule } from '@fantaz/shared/ui/skeleton';
import { LoggerService } from '@fantaz/shared/utils/log';
import { RouterUtil } from '@fantaz/shared/utils/router';
import { TournamentsFacade } from '@fantaz/tournaments-v2/data-access';
import { TournamentsUiFilterModule } from '@fantaz/tournaments-v2/ui/filters';
import { PageTabRouteModule } from '@fantaz/tournaments-v2/ui/page-tab-route';
import { TournamentsUiPromotionModule } from '@fantaz/tournaments-v2/ui/promotion';
import { TournamentsCardsViewModule } from '@fantaz/tournaments-v2/ui/tournaments-cards-view';
import { TournamentsTableViewModule } from '@fantaz/tournaments-v2/ui/tournaments-table-view';
import { TournamentsViewModeModule } from '@fantaz/tournaments-v2/ui/view-mode';
import { TournamentWidgetAdvertisementsModule } from '@fantaz/tournaments-v2/widgets/advertisements';
import { TournamentWidgetSliderModule } from '@fantaz/tournaments-v2/widgets/tournament-slider';
import { loadAdvertisement } from '@fantaz/tournaments/data-access';
import { GameListPickerModule } from '@fantaz/tournaments/ui/game-list-picker';
import { StatBoxComponent } from '@fantaz/tournaments/ui/stat-box';
import { ViewMode, ViewModeComponent } from '@fantaz/ui/view-mode';

import {
  createEntryAmountFilterOptions,
  createFilters,
  createGameFilterOptions,
  createPrizeFilterOptions,
  createStatusFilterOptions,
  createTagsFilterOptions,
  createTournamentTypeFilterOptions,
  mapTournamentEntryAmount,
  mapTournamentGame,
  mapTournamentPrize,
  mapTournamentStatus,
  mapTournamentTags,
  type SelectedFilerValues,
} from './tournaments-filter-utils';
import { toSignal } from '@angular/core/rxjs-interop';

export interface AllTournamentsLoaded {
  allCashLoaded: boolean;
  allPrizeLoaded: boolean;
}

type TournamentTypeSelection = FilterSelection<
  'type' | 'status' | 'tags' | 'games' | 'entryAmount' | 'prize' | 'q'
>;

@UntilDestroy()
@Component({
  selector: 'fantaz-list-tournaments',
  standalone: true,
  imports: [
    TournamentWidgetAdvertisementsModule,
    CommonModule,
    MaterialModule,
    GameListPickerModule,
    EmptyListModule,
    TournamentWidgetSliderModule,
    PageTabRouteModule,
    TournamentsViewModeModule,
    StatBoxComponent,
    TournamentsUiPromotionModule,
    TournamentsCardsViewModule,
    TournamentsTableViewModule,
    TournamentsUiFilterModule,
    Search,
    ShareModule,
    Filters,
    SkeletonModule,
    SortBy,
    ViewModeComponent,
    RefinementList,
  ],
  templateUrl: './list-tournaments.html',
  styleUrls: ['./list-tournaments.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TournamentsListFeature implements OnInit {
  readonly #bottomSheet = inject(MatBottomSheet);
  loaded$: Observable<boolean> = this.tournamentFacade.loaded$;

  filtering: Signal<boolean | undefined> = this.tournamentFacade.filtering;
  isLoading = this.tournamentFacade.loading;
  /**
   * Error
   */
  error$: Observable<string | null | undefined> = this.tournamentFacade.error$;
  errorLoadingMore$: Observable<string | null | undefined> =
    this.tournamentFacade.errorLoadingMore$;

  /**
   * Tournament items
   */
  tournaments$: Observable<Array<CasualTournamentEntity>> =
    this.tournamentFacade.allTournaments$;

  // Gets the pagination data from the tournament service
  pagination$: Observable<AllTournamentsPagination> =
    this.tournamentFacade.pagination$;
  pagination: AllTournamentsPagination;

  /**
   * Page view mode, available options: rows, cards
   */
  viewMode = signal<ViewMode>(ViewMode.cards);
  ViewMode = ViewMode;

  user$ = this.authFacade.currentUser$;
  user = toSignal(this.user$);
  profile$ = this.profileFacade.profile$.pipe(untilDestroyed(this));
  currentUser: CurrentUser | null | undefined;
  showFilters = signal<boolean>(false);
  filterSelected = false;
  filterModel: RoomFilterModel[] = [
    {
      filter: 'entryAmount',
      static: true,
      label: 'Entry Fee',
      symbol: '$',
    },
    {
      filter: 'prize',
      label: 'Prize',
      static: true,
      symbol: '$',
    },
  ];
  filterInitialState: RoomFilterModel[] = [];
  readonly TournamentType = TournamentType;
  scrollToTournaments = false;

  currentTournamentsParams: TournamentsQueryParams = {
    tournamentType: TournamentType.PRIZE,
    tournamentTags: [TournamentTagsEnum.PRIZE_TOURNAMENT],
    cashPageIndex: 0,
    prizePageIndex: 0,
    pageSize: 60,
  };
  lastTournamentType: TournamentType;
  tournamentPassesForUser: UserPasses[];
  passesLoaded = false;
  currentPage = {
    cash: 0,
    prize: 0,
  };
  profile: Profile | null;

  /**
   * Default component values
   */
  default = {
    search: '',
    filter: {
      entryAmount: [],
      prize: [],
      games: [],
      status: ['Active'],
      tournamentType: [TournamentType.PRIZE],
      tags: [],
    },
    pagination: {
      pageSize: 10,
      pageIndex: 1,
    },
  };
  filterQuery = injectQuery(() => ({
    queryKey: [
      'tournaments-filters',
      this.selectedStatus(),
      [],
      [],
      [],
      [],
      0,
      60,
      '',
      'Featured',
      'asc',
    ],
    queryFn: () =>
      lastValueFrom(
        this.tournamentApiService.fetchTournaments({
          tournamentType: TournamentType.ALL,
          activeTournaments: true,
          comingSoonOnly: true,
          retiredEvents: true,
          pageSize: 100,
          getEsRanges: true,
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: 1000 * 60 * 5,
  }));

  searchOpen = signal(false);

  /**
   * Games facet
   */
  games = computed(() => {
    const data = this.filterQuery.data();
    return data?.facets?.games || [];
  });

  /**
   * Tags facet
   */
  tags = computed<FaceItemModel[]>(() => {
    const data = this.filterQuery.data();
    return (data?.facets?.tags || []) as FaceItemModel[];
  });

  /**
   * Entry amount facet
   */
  entryAmounts = computed(() => {
    const data = this.filterQuery.data();
    return createFilters(data?.esRangeValues.entryAmountRanges || []);
  });

  /**
   * Prize facet
   */
  prizes = computed(() => {
    const data = this.filterQuery.data();
    return createFilters(data?.esRangeValues.prizeValueRanges || []);
  });

  /**
   * All facets
   */
  facets = computed<Facet>(() => {
    const facets = this.filterQuery.data()?.facets || {};
    return {
      ...createGameFilterOptions(this.games()),
      ...createEntryAmountFilterOptions(this.entryAmounts() || []),
      ...createPrizeFilterOptions(this.prizes() || []),
      ...createTournamentTypeFilterOptions(facets?.type || []),
      ...createTagsFilterOptions(facets?.tags || [], [...(facets.type || [])]),
      ...createStatusFilterOptions(facets?.status || []),
    };
  });

  /**
   * Page size
   */
  pageSize = signal(this.default.pagination.pageSize);

  /**
   * Page index
   */
  pageIndex = signal(this.default.pagination.pageIndex);

  // ***************************************************************************
  // *** Search and filters
  // ***************************************************************************
  /**
   * User filter selection.
   *
   * This is the state of the filters that the user has selected from the panel,
   * it is used to update the query params and fetch the data.
   */
  filters = signal<TournamentTypeSelection>({
    type: [],
    status: [],
    tags: [],
    games: [],
    entryAmount: [],
    prize: [],
    q: [],
  });

  /**
   * Selected tournament type
   */
  selectedTournamentType = computed<TournamentType>(() => {
    const tournamentType = this.filters()?.type || [];

    if (
      tournamentType.includes(TournamentType.CASH) &&
      tournamentType.includes(TournamentType.CASUAL)
    ) {
      return TournamentType.ALL;
    }

    if (tournamentType.length == 1) {
      return tournamentType[0] as TournamentType;
    }

    return TournamentType.ALL;
  });

  /**
   * Selected status
   */
  selectedStatus = computed<string[]>(() => {
    return this.filters()?.status?.length ? this.filters()?.status : ['Active'];
  });

  /**
   * Selected tags
   */
  selectedTags = computed<string[]>(() => {
    return [
      ...new Set([
        ...(this.filters()?.tags || []),
        ...(this.filters()?.type || []),
      ]),
    ];
  });

  /**
   * Selected games
   */
  selectedGames = computed<string[]>(() => {
    return this.filters()?.games || [];
  });

  /**
   * Selected entry amount
   */
  selectedEntryAmount = computed<string[]>(() => {
    return this.filters()?.entryAmount || [];
  });

  /**
   * Selected prize
   */
  selectedPrize = computed<string[]>(() => {
    return this.filters()?.prize || [];
  });

  sortOptions = signal<Record<string, string>>({
    Featured: 'featured',
    'Entry fees: Low to High': 'entry_fees_low',
    'Entry fees: High to Low': 'entry_fees_high',
    'Ending sooner': 'ending_sooner',
    'Greatest prizes': 'greatest_prizes',
  });

  /**
   * Sort by
   */
  sortBy = signal<string>('Featured');

  search = computed<string>(() => {
    const search = this.filters()?.q;

    if (Array.isArray(search)) {
      return search[0];
    }

    return search || '';
  });

  tournamentsQuery = injectInfiniteQuery(() => ({
    queryKey: [
      'tournaments',
      this.selectedTournamentType(),
      this.selectedStatus(),
      this.selectedGames(),
      this.selectedEntryAmount(),
      this.selectedPrize(),
      this.selectedTags(),
      this.search(),
      this.sortBy(),
    ],
    queryFn: ({ pageParam }) =>
      lastValueFrom(
        this.tournamentApiService.fetchTournaments({
          ...mapTournamentStatus(this.selectedStatus() as string[]),
          ...mapTournamentTags(this.selectedTags() as string[]),
          ...mapTournamentGame(this.selectedGames() as string[]),
          ...mapTournamentEntryAmount(this.selectedEntryAmount() as string[]),
          ...mapTournamentPrize(this.selectedPrize() as string[]),
          tournamentType: this.selectedTournamentType(),
          search: this.search(),
          pageSize: this.pageSize(),
          pageIndex: pageParam,
          sortBy: this.sortBy(),
        }),
      ),
    initialPageParam: 0,
    getPreviousPageParam: (firstPage) => firstPage.pageIndex - 1,
    getNextPageParam: (lastPage) => lastPage.pageIndex + 1,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: 1000 * 60 * 5,
    enabled: this.viewMode() === ViewMode.cards,
  }));

  hasTournaments = computed(() => {
    const pages = this.tournamentsQuery.data()?.pages || [];
    return pages.reduce((acc, page) => acc + page.items.length, 0) > 0;
  });

  // this is used to view the tournaments in the table view
  cashPageIndex = signal(0);
  cashPageSize = signal(20);
  showCashTournaments = computed(
    () =>
      this.filters().type.length == 0 || this.filters().type.includes('casual'),
  );
  cashTournamentsQuery = injectQuery(() => ({
    queryKey: [
      'tournaments-casual',
      TournamentType.CASUAL,
      this.selectedStatus(),
      this.selectedGames(),
      this.selectedEntryAmount(),
      this.selectedPrize(),
      this.selectedTags(),
      0,
      this.cashPageSize(),
      this.search(),
      this.sortBy(),
    ],
    queryFn: () =>
      lastValueFrom(
        this.tournamentApiService.fetchTournaments({
          ...mapTournamentStatus(this.selectedStatus() as string[]),
          ...mapTournamentTags(this.selectedTags() as string[]),
          ...mapTournamentGame(this.selectedGames() as string[]),
          ...mapTournamentEntryAmount(this.selectedEntryAmount() as string[]),
          ...mapTournamentPrize(this.selectedPrize() as string[]),
          tournamentType: TournamentType.CASUAL,
          search: this.search(),
          pageSize: this.cashPageSize(),
          pageIndex: this.cashPageIndex(),
          sortBy: this.sortBy(),
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: 1000 * 60 * 5,
    enabled: this.viewMode() === ViewMode.rows && this.showCashTournaments(),
  }));

  prizePageIndex = signal(0);
  prizePageSize = signal(20);
  showPrizeTournaments = computed(
    () =>
      this.filters().type.length == 0 || this.filters().type.includes('prize'),
  );
  prizeTournamentsQuery = injectQuery(() => ({
    queryKey: [
      'tournaments-prize',
      TournamentType.PRIZE,
      this.selectedStatus(),
      this.selectedGames(),
      this.selectedEntryAmount(),
      this.selectedPrize(),
      this.selectedTags(),
      this.prizePageIndex(),
      this.prizePageSize(),
      this.search(),
      this.sortBy(),
    ],
    queryFn: () =>
      lastValueFrom(
        this.tournamentApiService.fetchTournaments({
          ...mapTournamentStatus(this.selectedStatus() as string[]),
          ...mapTournamentTags(this.selectedTags() as string[]),
          ...mapTournamentGame(this.selectedGames() as string[]),
          ...mapTournamentEntryAmount(this.selectedEntryAmount() as string[]),
          ...mapTournamentPrize(this.selectedPrize() as string[]),
          tournamentType: TournamentType.PRIZE,
          search: this.search(),
          pageSize: this.prizePageSize(),
          pageIndex: this.prizePageIndex(),
          sortBy: this.sortBy(),
        }),
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: 1000 * 60 * 5,
    enabled: this.viewMode() === ViewMode.rows && this.showPrizeTournaments(),
  }));

  // ui
  searchExpanded = signal<boolean>(false);
  activeFilterCount = computed(() => {
    return Object.keys(this.filters()).reduce((acc, key) => {
      const val = this.filters()[key as keyof TournamentTypeSelection].filter(
        (v) => v.trim() !== '',
      );

      if (Array.isArray(val)) {
        return acc + val.length;
      }

      return acc;
    }, 0);
  });

  cardTournamentsMapped = signal<Record<string, CasualTournamentEntity[]>>({});
  cardTournaments = computed<CasualTournamentEntity[]>(() => {
    return Object.keys(this.cardTournamentsMapped()).reduce((acc, key) => {
      return [...acc, ...this.cardTournamentsMapped()[key]];
    }, [] as CasualTournamentEntity[]);
  });

  constructor(
    private readonly logger: LoggerService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private tournamentApiService: TournamentApiService,
    private store: Store,
    private platformLocation: PlatformLocation,
    private shareService: ShareService,
    private tournamentFacade: TournamentsFacade,
    private authFacade: AuthFacade,
    private profileFacade: ProfileFacade,
    private readonly appRouterPaths: AppRouterPaths,
    private readonly breadcrumbsService: BreadcrumbsService,
  ) {}

  ngOnInit(): void {
    this.init();
    this.getUserSettings();
    this.store.dispatch(loadAdvertisement());
  }

  getUserSettings() {
    const path = RouterUtil.Configuration.Tournaments || 'tournaments';
    localStorage.setItem('playPage', path);
    this.profile$.pipe(takeLast(1)).subscribe((profile) => {
      this.profile = profile;
      if (this.profile) {
        const settings = this.profile.userSettings
          ? cloneDeep(this.profile.userSettings)
          : {};
        if (this.profile.userSettings?.playPage !== path) {
          settings.playPage = path;
          const payload: Profile = {
            ...this.profile,
            userSettings: settings,
          };
          this.profileFacade.updateProfileSettings(payload, {
            silent: true,
          });
        }
      }
    });
  }

  init() {
    this.hydrateFilterFromQueryParams();
  }

  /**
   * Get route parameters
   *
   * @returns Route parameters
   */
  private getRouteParams() {
    // TODO: Add safe parameters using schema validation for all available
    // query parameters
    return this.router.routerState.snapshot.root.queryParams;
  }

  fetchMoreTournaments() {
    this.tournamentsQuery.fetchNextPage();
  }

  fetchPageTournaments(page: IPageModel, type: TournamentType) {
    switch (type) {
      case TournamentType.CASH:
        this.cashPageIndex.set(Number(page.pageIndex));
        this.cashPageSize.set(Number(page.pageSize));
        break;
      case TournamentType.PRIZE:
        this.prizePageIndex.set(Number(page.pageIndex));
        this.prizePageSize.set(Number(page.pageSize));
        break;
    }
  }

  /**
   * Opens the mobile filters in a bottom sheet.
   */
  private openMobileFilters() {
    const ref = this.#bottomSheet.open(MobileFiltersComponent, {
      panelClass: 'mat-bottom-sheet-container-medium',
      data: {
        facet: this.facets(),
        filters: this.filters(),
      },
    });

    ref.afterDismissed().subscribe((result) => {
      if (result && result.filters) {
        this.updateFilters(result.filters);
      }
    });
  }

  /**
   * Updates the filters based on the selected filters. If no filters are selected,
   * it will reset the filters to the default values.
   *
   * @param {SelectedFilerValues} filters - The selected filters.
   */
  private updateFilters(filters: FilterSelection) {
    const changes = Object.keys(filters).reduce((acc, key) => {
      return acc + filters[key].length;
    }, 0);

    if (changes === 0) {
      this.resetFilter();
      return;
    }

    this.filters.update((prev) => ({
      ...prev,
      ...filters,
    }));

    this.updateQueryParams(filters);
  }

  /**
   * Updates the query parameters based on the selected filters.
   * @param {SelectedFilerValues} queryParams - The selected filters.
   */
  private updateQueryParams(
    queryParams: Record<string, null | string | object>,
  ) {
    const params = this.getRouteParams();
    this.router.navigate([], {
      queryParams: {
        ...params,
        ...queryParams,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }

  /**
   * Hydrates the filter values from the query parameters.
   *
   * Transforms route params like:
   * Example 1: { status: 'active', games: [gameId1, gameId2, gameId3], tournamentTags: ['cash', 'prize'] }
   * Example 2: { status: 'active', games: 'gameId1', tournamentTags: 'cash' }
   *
   * Into format:
   * {
   *   status: { active: true },
   *   games: { gameId1: true, gameId2: true, gameId3: true },
   *   tournamentTags: { cash: true, prize: true }
   * }
   */
  private hydrateFilterFromQueryParams() {
    const params = this.getRouteParams();
    if (!params || Object.keys(params).length === 0) return;

    const transformedFilters = Object.keys(params).reduce((acc, key) => {
      const value = params[key];
      let val: string[] = [];

      if (value) {
        if (Array.isArray(value)) {
          val = [...new Set(value)];
        } else if (typeof value === 'string') {
          val = [value];
        }

        if (val.length > 0) {
          acc[key] = val;
        }
      }

      return acc;
    }, {} as SelectedFilerValues);

    this.filters.update((prev) => ({ ...merge(prev, transformedFilters) }));
  }

  /**
   * Updates the filter selections and fetches the tournaments.
   * @param {SelectedFilerValues} filters - The selected filters.
   */
  updateFilterSelections(filters: FilterSelection) {
    this.updateFilters(filters);
  }

  /**
   * Function to initialize view mode
   */
  private initViewMode() {
    const { viewMode } = this.getRouteParams();

    if (viewMode) {
      this.viewMode.set(viewMode == 'cards' ? ViewMode.cards : ViewMode.rows);
    }
  }

  /**
   * Function to handle view mode change
   *
   * @param mode
   */
  onViewModeChange(mode: string) {
    this.viewMode.set(mode as ViewMode);
  }

  /**
   * Handle games selection on game list picker component. Updates the filters.
   *
   * @param {string[]} games - The selected games.
   */
  gamesSelectionChanged(games: string[]) {
    this.updateFilters({
      games,
    });
  }

  /**
   * Handle tournament type selection on tournament type filter component.
   * Updates the filters.
   *
   * @param {string[]} selected - The selected tournament types.
   */
  tournamentTypeSelectionChange(selected: string[]) {
    const types = ['prize', 'casual'];

    if (selected.length == 0) {
      this.updateFilters({
        type: [],
        tags: [],
      });

      this.updateQueryParams({
        type: null,
        tags: null,
      });

      return;
    }

    const selectedTypes = selected.filter((type) => types.includes(type));
    const selectedTags = selected.filter((tag) => !types.includes(tag));

    this.updateFilters({
      ...(selectedTypes.length > 0 ? { type: selectedTypes } : { type: [] }),
      ...(selectedTags.length > 0 ? { tags: selectedTags } : { tags: [] }),
    });
  }

  /**
   * Handle tags selection on tags filter component. Updates the filters.
   *
   * @param {string[]} selected - The selected tags.
   */
  tagsSelectionChange(selected: string[]) {
    this.updateFilters({
      tags: selected,
    });
  }

  /**
   * Function to handle tournament card navigation
   * @param tournament
   */
  onTournamentCardPress(tournament: CasualTournamentEntity) {
    // add local state of router to breadcrumb to return to this page in case
    // of navigation
    this.breadcrumbsService.updateRoute('play', {
      href: window.location.href,
    });

    // the best option to use router path service is to use the resolve method
    // to generate the url and then use the router to navigate to the generated
    // url
    let url = this.appRouterPaths.resolve(
      this.appRouterPaths.routes.tournamentDetails,
      {
        type: 'cash',
        tournamentUrl: tournament.url,
      },
    );

    if (tournament.lifeExperience) {
      url = this.appRouterPaths.resolve(
        this.appRouterPaths.routes.tournamentEventDetails,
        {
          type: 'prizes',
          tournamentUrl: tournament.url,
          eventScheduleUuid: tournament.eventSchedules[0].uuid,
        },
      );
    }

    // navigate to the generated url
    this.appRouterPaths.navigateByUrl(url.toString(), { relative: true });
  }

  onTournamentShare(tournament: CasualTournamentEntity) {
    this.logger.log('[Casual Tournaments Share Tournament]', tournament);
    const baseURL = this.platformLocation.href
      .split('?')[0]
      .replace(
        RouterUtil.Configuration.TournamentsV2,
        RouterUtil.Configuration.Tournaments,
      );
    let urlStr = `${baseURL}${tournament.lifeExperience ? 'prizes' : 'cash'}/${
      tournament.url
    }`;

    if (tournament.lifeExperience) {
      urlStr += `/${tournament.eventSchedules[0].uuid}`;

      if (tournament.metadata?.vip === 'true') {
        urlStr = `${window.location.origin}/vip/${tournament.url}`;
      }
    }
    const url = new URL(urlStr);
    this.shareService.share({
      title: 'FantaZ',
      text: 'Play Casual tournaments',
      url: url.toString(),
      currentUser: this.user(),
      image: tournament.game.imageUrl,
    });
  }

  /**
   * Handles search change.
   *
   * @param {string} search - The search term.
   */
  onSearchChange(search: string) {
    this.filters.update((prev) => ({
      ...prev,
      q: [search],
    }));

    this.updateQueryParams({
      q: search ? search : null,
    });
  }

  gamesRoute() {
    const path = RouterUtil.Configuration.Games;
    window.location.href = `${window.location.origin}/${path}`;
  }

  /**
   * Reset filters. This will reset the filters to the default values.
   */
  resetFilter() {
    this.filters.set({
      type: [],
      status: [],
      tags: [],
      games: [],
      entryAmount: [],
      prize: [],
      q: [],
    });
    this.updateQueryParams({
      q: null,
      search: null,
      status: null,
      games: null,
      tournamentTags: null,
      tournamentType: null,
    });
  }

  searchPanelOpened(v: boolean) {
    this.searchOpen.set(v);
  }

  toggleFilterPanelVisibility() {
    if (window.innerWidth < 768) {
      this.openMobileFilters();
    } else {
      this.showFilters.update((v) => !v);
    }
  }

  closeFilterPanel() {
    this.showFilters.set(false);
  }

  onSortChange(sort: { value: string }) {
    this.sortBy.set(sort.value);

    this.updateQueryParams({
      sortBy: sort.value,
    });
  }

  toggleSearchExpanded(v: boolean) {
    this.searchExpanded.set(v);
  }
}
